import { useEffect, useId, useState } from "react";
import { CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { fetchMotivosCached } from "@/lib/motivosCache";
import {
  desfazerRegistroTentativa,
  registrarTentativa,
  type RegistrarTentativaResultado,
} from "@/lib/tentativas";
import type { ContactChannel, ContactResult, Lead, Motivo } from "@/lib/types";

interface RegistrarTentativaProps {
  lead: Lead;
  /** Chamado apos salvar (ou desfazer) com o lead ja atualizado, para o
   * chamador atualizar sua lista/cache local sem precisar refazer a busca. */
  onLeadAtualizado: (lead: Lead) => void;
  /** Fila usa colapsado por padrao (expande sob clique); a Ficha do lead usa
   * sempre expandido. Componente e o MESMO nos dois lugares — so a moldura
   * de expand/collapse muda. */
  startExpanded?: boolean;
}

type Passo = "acoes" | "confirmar_recusa";

const OPCOES_DATA = [
  { id: "amanha", label: "Amanhã", dias: 1 },
  { id: "3dias", label: "Em 3 dias", dias: 3 },
  { id: "7dias", label: "Em 7 dias", dias: 7 },
] as const;

function dataComOffset(dias: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  d.setHours(9, 0, 0, 0);
  return d;
}

export function RegistrarTentativa({
  lead,
  onLeadAtualizado,
  startExpanded = false,
}: RegistrarTentativaProps) {
  const { appUser } = useAuth();
  const { showToast } = useToast();

  const [expandido, setExpandido] = useState(startExpanded);
  const [passo, setPasso] = useState<Passo>("acoes");
  const [canal, setCanal] = useState<ContactChannel>("ligacao");
  const [nota, setNota] = useState("");
  const [dataOpcao, setDataOpcao] = useState<(typeof OPCOES_DATA)[number]["id"]>("amanha");
  const [dataCustom, setDataCustom] = useState<string | null>(null);
  const [motivos, setMotivos] = useState<Motivo[] | null>(null);
  const [motivoSelecionado, setMotivoSelecionado] = useState<number | null>(null);
  const [motivoOutroTexto, setMotivoOutroTexto] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const notaId = useId();
  const motivoOutroId = useId();
  const dataCustomId = useId();

  useEffect(() => {
    if (passo === "confirmar_recusa" && motivos === null) {
      fetchMotivosCached("recusa")
        .then(setMotivos)
        .catch(() => setErro("Não foi possível carregar os motivos de recusa."));
    }
  }, [passo, motivos]);

  function resetar() {
    setPasso("acoes");
    setNota("");
    setDataOpcao("amanha");
    setDataCustom(null);
    setMotivoSelecionado(null);
    setMotivoOutroTexto("");
    setErro(null);
  }

  function proximoContatoEscolhido(): string {
    if (dataCustom) return new Date(dataCustom).toISOString();
    const opcao = OPCOES_DATA.find((o) => o.id === dataOpcao) ?? OPCOES_DATA[0];
    return dataComOffset(opcao.dias).toISOString();
  }

  async function salvar(
    resultado: ContactResult,
    extra?: { motivoId?: number | null; motivoOutro?: string | null },
  ) {
    if (!appUser) return;
    setSalvando(true);
    setErro(null);
    try {
      const resultadoSalvo: RegistrarTentativaResultado = await registrarTentativa({
        lead,
        canal,
        resultado,
        autorId: appUser.id,
        nota,
        proximoContatoEm: resultado === "recusou" ? null : proximoContatoEscolhido(),
        motivoId: extra?.motivoId ?? null,
        motivoOutro: extra?.motivoOutro ?? null,
      });

      onLeadAtualizado(resultadoSalvo.leadAtualizado);
      resetar();
      if (!startExpanded) setExpandido(false);

      showToast({
        message: `Tentativa registrada: ${lead.empresa} — ${LABEL_RESULTADO[resultado]}.`,
        tone: "success",
        actionLabel: "Desfazer",
        onAction: () => {
          desfazerRegistroTentativa({
            attemptId: resultadoSalvo.attempt.id,
            leadId: lead.id,
            autorId: appUser.id,
            leadAnteriorPatch: resultadoSalvo.leadAnteriorPatch,
          })
            .then(() => {
              onLeadAtualizado({
                ...resultadoSalvo.leadAtualizado,
                ...resultadoSalvo.leadAnteriorPatch,
              } as Lead);
              showToast({ message: "Registro desfeito.", tone: "neutral" });
            })
            .catch((e: unknown) => {
              showToast({
                message: `Não foi possível desfazer: ${(e as Error).message}`,
                tone: "error",
              });
            });
        },
      });
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  if (lead.nao_contatar) {
    return (
      <div className="field-hint">
        Este lead está marcado como <strong>Não contatar</strong>. Novas
        tentativas de contato estão bloqueadas.
      </div>
    );
  }

  if (!expandido) {
    return (
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        onClick={() => setExpandido(true)}
      >
        <CheckCircle2 className="icon" size={16} aria-hidden="true" />
        Registrar tentativa
      </button>
    );
  }

  const whatsappPermitido = lead.canal_permitido === "ligacao_e_whatsapp";

  return (
    <div>
      <fieldset>
        <legend>Canal</legend>
        <div className="chip-group" role="radiogroup" aria-label="Canal de contato">
          <button
            type="button"
            role="radio"
            aria-checked={canal === "ligacao"}
            className="chip"
            onClick={() => setCanal("ligacao")}
          >
            <Phone className="icon" size={16} aria-hidden="true" />
            <span className="chip-text">Ligação</span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={canal === "whatsapp"}
            className="chip"
            disabled={!whatsappPermitido}
            title={
              whatsappPermitido
                ? undefined
                : "WhatsApp libera automaticamente após uma resposta registrada (atendeu/reagendou)."
            }
            onClick={() => setCanal("whatsapp")}
          >
            <MessageCircle className="icon" size={16} aria-hidden="true" />
            <span className="chip-text">WhatsApp {!whatsappPermitido && "(bloqueado)"}</span>
          </button>
        </div>
      </fieldset>

      {passo === "acoes" && (
        <>
          <fieldset>
            <legend>Próximo contato sugerido</legend>
            <div className="chip-group" role="radiogroup" aria-label="Data do próximo contato">
              {OPCOES_DATA.map((opcao) => (
                <button
                  key={opcao.id}
                  type="button"
                  role="radio"
                  aria-checked={dataOpcao === opcao.id && !dataCustom}
                  className="chip"
                  onClick={() => {
                    setDataOpcao(opcao.id);
                    setDataCustom(null);
                  }}
                >
                  <span className="chip-text">{opcao.label}</span>
                </button>
              ))}
            </div>
            <label htmlFor={dataCustomId} className="field-hint" style={{ marginTop: 8 }}>
              Ou escolha uma data específica
            </label>
            <input
              id={dataCustomId}
              type="datetime-local"
              value={dataCustom ?? ""}
              onChange={(e) => setDataCustom(e.target.value || null)}
            />
          </fieldset>

          <div className="field">
            <label htmlFor={notaId}>Nota (opcional)</label>
            <textarea
              id={notaId}
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={2}
            />
          </div>

          {erro && (
            <p className="field-error" role="alert">
              {erro}
            </p>
          )}

          <div className="chip-group">
            <button
              type="button"
              className="chip chip--outcome-success"
              disabled={salvando}
              onClick={() => salvar("atendeu")}
            >
              <span className="chip-text">Atendeu</span>
            </button>
            <button
              type="button"
              className="chip chip--outcome-neutral"
              disabled={salvando}
              onClick={() => salvar("nao_atendeu")}
            >
              <span className="chip-text">Não atendeu</span>
            </button>
            <button
              type="button"
              className="chip chip--outcome-neutral"
              disabled={salvando}
              onClick={() => salvar("pediu_retorno")}
            >
              <span className="chip-text">Reagendou</span>
            </button>
            <button
              type="button"
              className="chip chip--outcome-danger"
              disabled={salvando}
              onClick={() => setPasso("confirmar_recusa")}
            >
              <span className="chip-text">Recusou</span>
            </button>
            <details>
              <summary className="btn btn--ghost btn--sm" style={{ display: "inline-flex" }}>
                Mais opções
              </summary>
              <div className="chip-group" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="chip chip--outcome-neutral"
                  disabled={salvando}
                  onClick={() => salvar("caixa_postal")}
                >
                  <span className="chip-text">Caixa postal</span>
                </button>
                <button
                  type="button"
                  className="chip chip--outcome-neutral"
                  disabled={salvando}
                  onClick={() => salvar("numero_invalido")}
                >
                  <span className="chip-text">Número inválido</span>
                </button>
              </div>
            </details>
            {!startExpanded && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  resetar();
                  setExpandido(false);
                }}
              >
                Cancelar
              </button>
            )}
          </div>
        </>
      )}

      {passo === "confirmar_recusa" && (
        <>
          <fieldset>
            <legend>Motivo da recusa</legend>
            {motivos === null && <p className="field-hint">Carregando motivos…</p>}
            {motivos && (
              <div className="chip-group" role="radiogroup" aria-label="Motivo da recusa">
                {motivos.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    role="radio"
                    aria-checked={motivoSelecionado === m.id}
                    className="chip"
                    onClick={() => setMotivoSelecionado(m.id)}
                  >
                    <span className="chip-text">{m.rotulo}</span>
                  </button>
                ))}
              </div>
            )}
          </fieldset>

          {motivos?.find((m) => m.id === motivoSelecionado)?.exige_detalhe && (
            <div className="field">
              <label htmlFor={motivoOutroId}>Detalhe (obrigatório para "Outro")</label>
              <textarea
                id={motivoOutroId}
                value={motivoOutroTexto}
                onChange={(e) => setMotivoOutroTexto(e.target.value)}
                rows={2}
              />
            </div>
          )}

          {erro && (
            <p className="field-error" role="alert">
              {erro}
            </p>
          )}

          <div className="fila-item__actions">
            <button
              type="button"
              className="btn btn--danger btn--sm"
              disabled={
                salvando ||
                !motivoSelecionado ||
                (motivos?.find((m) => m.id === motivoSelecionado)?.exige_detalhe &&
                  motivoOutroTexto.trim() === "")
              }
              onClick={() =>
                salvar("recusou", {
                  motivoId: motivoSelecionado,
                  motivoOutro: motivoOutroTexto.trim() || null,
                })
              }
            >
              Confirmar recusa
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={salvando}
              onClick={() => setPasso("acoes")}
            >
              Voltar
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const LABEL_RESULTADO: Record<ContactResult, string> = {
  atendeu: "Atendeu",
  nao_atendeu: "Não atendeu",
  caixa_postal: "Caixa postal",
  numero_invalido: "Número inválido",
  pediu_retorno: "Reagendou",
  recusou: "Recusou",
};
